import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useT } from '../i18n/LocaleContext'
import { localizedPath } from '../i18n/routes'

type Props = {
  breadcrumb: string
  badge: string
  title: ReactNode
  intro?: ReactNode
  updatedAt: string
  children: ReactNode
}

export default function LegalLayout({
  breadcrumb,
  badge,
  title,
  intro,
  updatedAt,
  children,
}: Props) {
  const { t, locale } = useT()
  return (
    <main className="relative pt-28 sm:pt-32 pb-24 overflow-hidden isolate">
      {/* Background DA */}
      <div aria-hidden className="absolute inset-x-0 top-0 h-[700px] -z-10 pointer-events-none">
        <div
          className="absolute inset-x-0 -top-20 h-[500px]"
          style={{
            background:
              'radial-gradient(ellipse at top, rgba(59,93,255,0.18), rgba(59,93,255,0.04) 45%, transparent 75%)',
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.25]"
          style={{
            backgroundImage: 'radial-gradient(rgba(59,93,255,0.12) 1px, transparent 1px)',
            backgroundSize: '28px 28px',
            maskImage: 'radial-gradient(ellipse at center, black 25%, transparent 75%)',
          }}
        />
      </div>

      {/* Header */}
      <section className="relative max-w-3xl mx-auto px-5 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-xs sm:text-sm text-ink-500"
        >
          <Link to={localizedPath('home', locale)} className="hover:text-accent-700 transition-colors">
            {t('legalLayout.home')}
          </Link>
          <span className="mx-2 text-ink-300">/</span>
          <span className="text-ink-700">{breadcrumb}</span>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.05 }}
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-accent-200 bg-accent-50/80 backdrop-blur-sm px-3 py-1 text-xs text-accent-800"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full rounded-full bg-accent-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-600" />
          </span>
          <span className="font-medium">{badge}</span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
          className="mt-5 font-semibold tracking-[-0.03em] text-ink-950 text-[clamp(2rem,5vw,3.5rem)] leading-[1.05]"
        >
          {title}
        </motion.h1>

        {intro && (
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-5 text-base sm:text-lg text-ink-600 leading-relaxed"
          >
            {intro}
          </motion.p>
        )}

        <p className="mt-6 text-xs text-ink-500">
          {t('legalLayout.lastUpdated')}&nbsp;: <span className="text-ink-700 font-medium">{updatedAt}</span>
        </p>
      </section>

      {/* Body */}
      <section className="relative max-w-3xl mx-auto px-5 sm:px-6 mt-10">
        <article className="article-prose">
          <div>{children}</div>
        </article>
      </section>
    </main>
  )
}
